import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

const DataUserAnimation = () => {
  const controls = useAnimationControls();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const runAnimation = async () => {
      try {
        await controls.start("reset");
        await controls.start("animate");
        await new Promise(resolve => setTimeout(resolve, 5000));
        if (isMounted) {
          runAnimation();
        }
      } catch (error) {
        console.error("Animation error:", error);
      }
    };
    
    runAnimation();
  }, [isMounted, controls]);

  return (
    <div className="relative w-full overflow-hidden">
      <motion.div 
        className="bg-[#EDE9FE] rounded-xl p-4 sm:p-6 relative h-[500px] sm:h-[400px] flex flex-col sm:flex-row items-center transform-gpu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Container that changes from column (mobile) to row (desktop) */}
        <div className="flex flex-col sm:flex-row items-center w-full justify-center scale-[0.9] sm:scale-90 origin-top sm:origin-center">
          {/* User Figure */}
          <div className="relative mb-2 sm:mb-0 sm:mr-4 flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={controls}
              variants={{
                reset: { opacity: 0, scale: 0.8 },
                animate: { opacity: 1, scale: 1, transition: { delay: 0.3 } }
              }}
              className="flex flex-col items-center"
            >
              <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-700 text-center mt-1">Data<br/>User</span>
            </motion.div>
          </div>

          {/* Main Dashboard Container */}
          <motion.div 
            className="bg-white rounded-lg shadow-lg w-full max-w-5xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              reset: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0, transition: { delay: 1 } }
            }}
          >
            {/* Dashboard Header */}
            <div className="bg-purple-600 p-3 flex items-center">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-white text-sm ml-3">Data Asset Explorer</span>
            </div>

            {/* Dashboard Content */}
            <div className="p-3 sm:p-4 space-y-3">
              {/* Search Bar */}
              <motion.div
                className="relative"
                initial={{ opacity: 0 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0 },
                  animate: { opacity: 1, transition: { delay: 1.5 } }
                }}
              >
                <input
                  type="text"
                  className="w-full bg-gray-50 rounded-lg px-4 py-2 text-sm border border-gray-200"
                  placeholder="Search data assets..."
                  readOnly
                />
                <div className="absolute right-3 top-2.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </motion.div>

              {/* Data Assets Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { 
                    title: 'Customer Reports', 
                    quality: '98%', 
                    status: 'Created', 
                    owner: 'You',
                    type: 'generated'
                  },
                  { 
                    title: 'Sales Reports', 
                    quality: '95%', 
                    status: 'Shared', 
                    owner: 'Sales Team',
                    type: 'shared'
                  },
                ].map((asset, i) => (
                  <motion.div
                    key={i}
                    className="bg-gray-50 rounded-lg p-3"
                    initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                    animate={controls}
                    variants={{
                      reset: { opacity: 0, x: i % 2 === 0 ? -20 : 20 },
                      animate: { opacity: 1, x: 0, transition: { delay: 2 + (i * 0.2) } }
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-700">{asset.title}</span>
                        <span className="text-xs text-gray-500">Owner: {asset.owner}</span>
                      </div>
                      <motion.div
                        className={`px-2 py-1 rounded-full text-xs ${
                          asset.type === 'generated' 
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                        initial={{ scale: 0 }}
                        animate={controls}
                        variants={{
                          reset: { scale: 0 },
                          animate: { scale: 1, transition: { delay: 2.2 + (i * 0.2) } }
                        }}
                      >
                        {asset.status}
                      </motion.div>
                    </div>
                    <motion.div
                      className="h-1.5 bg-indigo-200 rounded-full overflow-hidden"
                      initial={{ width: 0 }}
                      animate={controls}
                      variants={{
                        reset: { width: 0 },
                        animate: { width: "100%", transition: { delay: 2.4 + (i * 0.2) } }
                      }}
                    >
                      <div 
                        className="h-full bg-indigo-600 rounded-full"
                        style={{ width: asset.quality }}
                      />
                    </motion.div>
                    <div className="text-xs text-gray-500 mt-1">Quality Score: {asset.quality}</div>
                  </motion.div>
                ))}
              </div>

              {/* Recent Activity */}
              <motion.div
                className="bg-gray-50 rounded-lg p-3"
                initial={{ opacity: 0, y: 20 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0, transition: { delay: 2.8 } }
                }}
              >
                <h3 className="text-sm font-medium text-gray-700 mb-2">Recent Activity</h3>
                <div className="space-y-2">
                  {[
                    {
                      text: 'Marketing Team shared "Campaign Performance" dataset',
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      )
                    },
                    {
                      text: 'Data quality observers executed successfully',
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      text: 'Query "Monthly Revenue Analysis" completed',
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )
                    }
                  ].map((activity, i) => (
                    <motion.div
                      key={i}
                      className="text-xs text-gray-600 flex items-center"
                      initial={{ opacity: 0, x: -10 }}
                      animate={controls}
                      variants={{
                        reset: { opacity: 0, x: -10 },
                        animate: { opacity: 1, x: 0, transition: { delay: 3 + (i * 0.2) } }
                      }}
                    >
                      <div className="w-5 h-5 bg-white rounded-full shadow-sm flex items-center justify-center mr-2">
                        {activity.icon}
                      </div>
                      {activity.text}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Processing Indicator */}
        <motion.div
          className="absolute bottom-2 right-2 flex space-x-1"
          animate={{
            opacity: [0.3, 1, 0.3],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: "linear"
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1 h-1 bg-purple-500 rounded-full"
              style={{
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DataUserAnimation; 